import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Checkbox, Flex, FormControl, FormLabel, Grid, GridItem, Heading, Input, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Select, Text } from '@chakra-ui/react'
import { UiSelect } from 'components/common'
import { getBackWrapPricingConfig, getCommunicationOptions, getConfigEmcPricing, getSideVentPricingConfig } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { CABINET_FACES, calculateQuoteEmcCost, EMC_FACES, EMC_MODELS, EMC_PITCHES, parseStructureComponents, STACK_MODES, STRUCTURE_COMPONENT_TYPES } from 'lib'
import { ExtraStructureComponents } from './ExtraStructureComponents'
import { StackModeField } from './StackModeField'

export const EdgeEmcForm = ({ form, setForm, ...rest }) => {
  const { createToast } = useAppContext()
  // const [modsH, setModsH] = useState(1)
  // const [modsL, setModsL] = useState(5)
  // const [alternateOptions, setAlternateOptions] = useState([])
  const [emcPricingConfig, setEmcPricingConfig] = useState(null)
  const [backWrapConfig, setBackWrapConfig] = useState({
    backWrapBase: 0,
    backWrapPerSqft: 0
  })
  const [sideVentConfig, setSideVentConfig] = useState({
    sideVentBase: 0,
    sideVentPerFoot: 0
  })

  useEffect(() => {
    const load = async () => {
      try {
        const [resBackWrap, resSideVent, resEmcPricingConfig] = await Promise.all([
          getBackWrapPricingConfig(),
          getSideVentPricingConfig(),
          getConfigEmcPricing()
        ])
        setBackWrapConfig(resBackWrap?.data ?? resBackWrap)
        setSideVentConfig(resSideVent?.data ?? resSideVent)
        setEmcPricingConfig(resEmcPricingConfig?.data ?? resEmcPricingConfig)
      } catch (err) {
        createToast('error', 'Error', err.response.data)
      }
    }
    load()
  }, [])

  // useEffect(() => {
  //   setForm(draft => { draft.emcPricing = null })

  //   if (!(typeof modsH === 'number' && modsH >= 0 && typeof modsL === 'number' && modsL >= 0)) return

  //   const load = async () => {
  //     try {
  //       const { rows } = await getDisplayPricings(1, 20, '', 'keyNo', 'asc', modsH, modsL)
  //       setAlternateOptions(rows)
  //       setForm(draft => { draft.emcAlternateOptions = [] })
  //     } catch (err) {
  //       createToast('error', 'Error', err.response.data)
  //     }
  //   }
  //   load()
  // }, [modsH, modsL])

  useEffect(() => {
    let displayPricingCost = 0

    // if (form.emcPricing && form.emcFace) {
    //   if (form.emcFace === EMC_FACES.SF) {
    //     displayPricingCost = form.emcPricing.sfPrice || 0
    //   }
    //   if (form.emcFace === EMC_FACES.DF || form.emcFace === EMC_FACES.DSF) {
    //     displayPricingCost = form.emcPricing.dfPrice || 0
    //   }
    // }

    displayPricingCost = calculateQuoteEmcCost(emcPricingConfig, form.emcModel, form.emcPitch, form.emcModsH, form.emcModsL, form.emcFace)
    if (form.emcCommunicationOption) {
      displayPricingCost += form.emcCommunicationOption.cost
    }
    setForm(draft => {
      draft.emcCost = parseFloat((displayPricingCost + (form.emcBackWrapCost || 0) + (form.emcSideVentCost || 0)).toFixed(2))
    })
  }, [form.emcModel, form.emcPitch, form.emcModsH, form.emcModsL, form.emcFace, form.emcBackWrapCost, form.emcSideVentCost, form.emcCommunicationOption?.id])

  const calculateBackWrapCost = () => {
    return backWrapConfig.backWrapBase + form.emcModsH * form.emcModsL * backWrapConfig.backWrapPerSqft
  }

  const calculateSideVentCost = () => {
    return sideVentConfig.sideVentBase + form.emcModsH * sideVentConfig.sideVentPerFoot
  }

  // const loadPricingOptions = useCallback((page) => getDisplayPricings(page, 20, '', 'keyNo', 'asc', modsH, modsL), [modsH, modsL])
  const loadCommunicationOptions = useCallback((page, search) => getCommunicationOptions(page, 20, search), [])

  const modelVisibility = emcPricingConfig?.modelVisibility ?? { g4: true, g5: true }
  const visibleModels = [
    ...(modelVisibility.g4 !== false ? [EMC_MODELS.G4] : []),
    ...(modelVisibility.g5 !== false ? [EMC_MODELS.G5] : [])
  ]

  useEffect(() => {
    if (form.noEmc || !form.emcModel) return
    if (!visibleModels.includes(form.emcModel)) {
      setForm(draft => { draft.emcModel = visibleModels[0] || null })
    }
  }, [visibleModels.join(','), form.noEmc, form.emcModel])

  return (
    <Box {...rest}>
      <Grid
        templateColumns="5fr 1fr"
        rowGap={5}
        columnGap={10}
        w="900px"
        maxW="100%"
      >
        <GridItem>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Heading size="md">Display Options</Heading>
            <Checkbox
              borderColor="gray.400"
              isChecked={form.noEmc}
              onChange={(e) => setForm(draft => {
                draft.noEmc = e.target.checked
                if (draft.noEmc) {
                  draft.emcModel = null
                  draft.emcPricing = null
                  draft.emcPricingId = 0
                  draft.emcFace = ''
                  draft.emcModsH = 0
                  draft.emcModsL = 0
                  draft.emcPitch = 0
                  draft.emcBackWrapChecked = false
                  draft.emcBackWrapCost = 0
                  draft.emcSideVentChecked = false
                  draft.emcSideVentCost = 0
                  draft.emcCommunicationOption = null
                  draft.emcCommunicationOptionId = 0
                  draft.emcAlternateOptions = []
                  draft.emcCost = 0
                  draft.emcStackMode = STACK_MODES.Stacked
                  draft.structureComponents = parseStructureComponents(draft.structureComponents).filter(item => item.type !== STRUCTURE_COMPONENT_TYPES.Emc)
                }
              })}
            >
              No EMC
            </Checkbox>
          </Box>
        </GridItem>
        <GridItem></GridItem>
        {!form.noEmc && (
          <>
            <GridItem>
              <Grid
                templateColumns="repeat(4, 1fr)"
                gap={4}
              >
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Model</FormLabel>
                    <Select
                      value={visibleModels.includes(form.emcModel) ? form.emcModel : ''}
                      onChange={e => setForm(draft => { draft.emcModel = e.target.value })}
                      placeholder="Select"
                    >
                      {visibleModels.map(model => (
                        <option key={model} value={model}>{model}</option>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Pitch</FormLabel>
                    <Select
                      value={form.emcPitch}
                      onChange={e => setForm(draft => { draft.emcPitch = e.target.value })}
                      placeholder="Select"
                    >
                      <option value={EMC_PITCHES.P15}>{EMC_PITCHES.P15}</option>
                      <option value={EMC_PITCHES.P9}>{EMC_PITCHES.P9}</option>
                      <option value={EMC_PITCHES.P6}>{EMC_PITCHES.P6}</option>
                      <option value={EMC_PITCHES.P4}>{EMC_PITCHES.P4}</option>
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Module Height (ft)</FormLabel>
                    <NumberInput
                      onChange={v => setForm(draft => { draft.emcModsH = parseInt(v) })}
                      value={form.emcModsH}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Module Length (ft)</FormLabel>
                    <NumberInput
                      onChange={v => setForm(draft => { draft.emcModsL = parseInt(v) })}
                      value={form.emcModsL}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </GridItem>
                {/* Row 2: Faces, Communication options - same line */}
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Faces</FormLabel>
                    <Select
                      value={form.emcFace}
                      onChange={e => setForm(draft => {
                        draft.emcFace = e.target.value
                        if (draft.emcFace === EMC_FACES.SF) {
                          draft.emcSideVentChecked = false
                          draft.emcSideVentCost = 0
                          draft.cabinetFace = CABINET_FACES.SF
                        } else if (draft.emcFace === EMC_FACES.DF || draft.emcFace === EMC_FACES.DSF) {
                          if (draft.emcFace === EMC_FACES.DF) {
                            draft.cabinetFace = CABINET_FACES.DF
                          } else {
                            draft.cabinetFace = ''
                          }
                          draft.emcBackWrapChecked = false
                          draft.emcBackWrapCost = 0
                        } else {
                          draft.emcSideVentChecked = false
                          draft.emcSideVentCost = 0
                          draft.emcBackWrapChecked = false
                          draft.emcBackWrapCost = 0
                        }
                      })}
                      placeholder="Select"
                    >
                      <option value={EMC_FACES.SF}>{EMC_FACES.SF}</option>
                      <option value={EMC_FACES.DF}>{EMC_FACES.DF}</option>
                      <option value={EMC_FACES.DSF}>{EMC_FACES.DSF}</option>
                    </Select>
                  </FormControl>
                  {form.emcFace === EMC_FACES.SF && (
                    <Checkbox
                      mt={2}
                      isChecked={form.emcBackWrapChecked}
                      onChange={(e) => setForm(draft => {
                        draft.emcBackWrapChecked = e.target.checked
                        draft.emcBackWrapCost = draft.emcBackWrapChecked ? calculateBackWrapCost() : 0
                      })}
                    >
                      Add Back Wrap ({calculateBackWrapCost()})
                    </Checkbox>
                  )}
                  {(form.emcFace === EMC_FACES.DF || form.emcFace === EMC_FACES.DSF) && (
                    <Checkbox
                      mt={2}
                      isChecked={form.emcSideVentChecked}
                      onChange={(e) => setForm(draft => {
                        draft.emcSideVentChecked = e.target.checked
                        draft.emcSideVentCost = draft.emcSideVentChecked ? calculateSideVentCost() : 0
                      })}
                    >
                      Add Side Vent ({calculateSideVentCost()})
                    </Checkbox>
                  )}
                </GridItem>
                <GridItem colSpan={3}>
                  <FormControl isRequired>
                    <FormLabel>Communication options</FormLabel>
                    <UiSelect
                      value={form.emcCommunicationOption}
                      onChange={v => {
                        setForm(draft => { draft.emcCommunicationOption = v })
                      }}
                      loadData={loadCommunicationOptions}
                      apiRespProp="rows"
                      getOptionValue={v => v.id}
                      getOptionLabel={v => `${v.description} - $${v.cost}`}
                      placeholder="Search"
                      noOptionsMessage={() => 'No Communication Options Found'}
                      isSearchable={false}
                      required={true}
                    />
                  </FormControl>
                </GridItem>
                {/* <GridItem colSpan={2}>
                  <FormControl isRequired>
                    <FormLabel>Matching display options</FormLabel>
                    <UiSelect
                      value={form.emcPricing}
                      onChange={v => {
                        setForm(draft => {
                          draft.emcPricing = v
                          draft.emcAlternateOptions = []
                        })
                      }}
                      loadData={loadPricingOptions}
                      apiRespProp="rows"
                      getOptionValue={v => v.id}
                      getOptionLabel={v => `Key no: ${v.keyNo} ${'\u00A0\u00A0\u00A0'} Pitch: ${v.pitch} ${'\u00A0\u00A0\u00A0'} Matrix: ${v.calculatedBoardMatrix} ${'\u00A0\u00A0\u00A0'} Size: ${v.dimsH} * ${v.dimsW}`}
                      placeholder="Search"
                      noOptionsMessage={() => 'No Display Options Found'}
                      isSearchable={false}
                      required={true}
                    />
                  </FormControl>
                </GridItem> */}
                <GridItem colSpan={4}>
                  <StackModeField
                    value={form.emcStackMode || STACK_MODES.Stacked}
                    onChange={(v) => setForm(draft => { draft.emcStackMode = v })}
                    label="Structure Position"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Stacked EMCs add their module height (1 module = 1 ft) to overall structure height. Side-by-side EMCs are excluded from height.
                  </Text>
                </GridItem>
                <GridItem colSpan={4}>
                  <FormControl>
                    <FormLabel>Include Alternate Options</FormLabel>
                    <Flex gap={5}>
                      {Object.values(EMC_PITCHES).filter(option => option !== form.emcPitch).map(option => {
                        const curCheckedStatus = form.emcAlternateOptions.includes(option)
                        return (
                          <Checkbox
                            key={option}
                            isChecked={curCheckedStatus}
                            onChange={() => setForm(draft => {
                              draft.emcAlternateOptions = curCheckedStatus ? form.emcAlternateOptions.filter(v => v !== option) : [...form.emcAlternateOptions, option]
                            })}
                          >
                            {option}
                          </Checkbox>
                        )
                      })}
                    </Flex>
                  </FormControl>
                </GridItem>
                {/* {alternateOptions.length !== 0 && (
                  <GridItem colSpan={2}>
                    <FormControl>
                      <FormLabel>Include Alternate Options</FormLabel>
                      <Flex gap={5}>
                        {alternateOptions.filter(option => option.id !== form.emcPricing?.id).map(option => {
                          const curCheckedStatus = form.emcAlternateOptions.indexOf(option.id) !== -1
                          return (
                            <Checkbox
                              key={option.id}
                              isChecked={curCheckedStatus}
                              onChange={() => setForm(draft => {
                                draft.emcAlternateOptions = curCheckedStatus ? form.emcAlternateOptions.filter(v => v !== option.id) : [...form.emcAlternateOptions, option.id]
                              })}
                            >
                              {option.pitch}
                            </Checkbox>
                          )
                        })}
                      </Flex>
                    </FormControl>
                  </GridItem>
                )} */}
              </Grid>
              <ExtraStructureComponents form={form} setForm={setForm} type={STRUCTURE_COMPONENT_TYPES.Emc} />
            </GridItem>
            <GridItem display="flex" alignItems="flex-end">
              <FormControl>
                <FormLabel>Display Total</FormLabel>
                <Input name="emc-total" value={form.emcCost} readOnly />
              </FormControl>
            </GridItem>
          </>
        )}
      </Grid>
    </Box>
  )
}

EdgeEmcForm.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}
